import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { getMarket, getBranch, getServiceInfo, getShowroom, getBeta } from '../../../../actions/location-actions';
import { Button, Column, Row, Select, TextArea, TextInput } from '@pella/sash-framework';
import { getLocalStorage, savePostalCode, getSessionStorage } from '../../../utilities/functions';
import styles from './index.module.scss';
import LoadingOverlay from '../../loading-overlay';
import EloquaForm from '../../eloqua-form';
import VisualJourneyModal from '../../../page/support/visual-journey-modal';
import { isValidEmail, isValidPhoneNumber, isValidPostal } from '../../../utilities/validations';
import { eloquaTrackingUrl } from '../../../utilities/constants';

const ServiceRequest = ({ path, utm }) => {
	const [values, setValues] = useState({ serviceZip: getLocalStorage('UserPostalCode') || '' });
	const [errors, setErrors] = useState({});
	const [serviceInfo, setServiceInfo] = useState({ phone: '', email: '' });
	const [status, setStatus] = useState('');
	const [update, setUpdate] = useState(false);
	const [step, setStep] = useState(1);
	const [modalOpen, setModalOpen] = useState(null);
	const [journeyOptOut, setJourneyOptOut] = useState(false);
	const dispatch = useDispatch();
	const locations = useSelector((state) => state.locations);

	useEffect(() => {
		if (getSessionStorage('journeyOptOut')) {
			setJourneyOptOut(true);
		}
	}, []);

	useEffect(() => {
		if (locations?.service_info?.service) {
			setServiceInfo({
				phone: locations.service_info.service.phone,
				email: locations.service_info.service.email,
			});
		}
	}, [locations?.service_info?.service]);

	useEffect(() => {
		if (step === 2 && status === 'loading') {
			if (!journeyOptOut && locations?.beta?.features?.[0] && locations.beta.features.includes('VisualJourney')) {
				setModalOpen(true);
			} else {
				setStep(3);
				setStatus('');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- possibly update
	}, [locations.beta]);

	useEffect(() => {
		if (update) {
			setUpdate(false);
		}
	}, [update]);

	const onInputChange = (event) => {
		setValues({
			...values,
			[event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
		});
		setStatus('');
		setUpdate(true);
	};

	const onNumericInputChange = (name, value) => {
		setValues({ ...values, [name]: value });
		setStatus('');
		setUpdate(true);
	};

	const onEmailBlur = (e) => {
		let errors = [];

		if (!isValidEmail(e.target.value)) {
			errors.emailAddress = 'Please enter a valid email address';
		}

		setErrors(errors);
	};

	const validateStep1 = (firstName, lastName, emailAddress) => {
		let errors = [];

		if (!firstName) {
			errors.firstName = 'Please enter your first name';
		}

		if (!lastName) {
			errors.lastName = 'Please enter your last name';
		}

		if (!isValidEmail(emailAddress)) {
			errors.emailAddress = 'Please enter a valid email address';
		}

		return errors;
	};

	const onNextButtonClick = () => {
		setStatus('loading');

		if (step === 1) {
			const errors = validateStep1(values.firstName, values.lastName, values.emailAddress);
			if (Object.keys(errors).length > 0) {
				setErrors(errors);
				setStatus('');
			} else {
				setStatus('');
				setStep(2);
			}
		} else if (step === 2) {
			const errors = validateStep2(values.primaryFirstName, values.primaryLastName, values.primaryPhone, values.serviceAddress, values.serviceCity, values.serviceState, values.serviceZip);
			if (Object.keys(errors).length > 0) {
				setErrors(errors);
				setStatus('');
			} else {
				savePostalCode(values.serviceZip);
				dispatch(getMarket(values.serviceZip));
				dispatch(getBranch(values.serviceZip));
				dispatch(getShowroom(values.serviceZip));
				dispatch(getServiceInfo(values.serviceZip));
				dispatch(getBeta(values.serviceZip));
			}
		}
	};

	const validateStep2 = (primaryFirstName, primaryLastName, primaryPhone, serviceAddress, serviceCity, serviceState, serviceZip) => {
		let errors = [];

		if (values.primaryResident && values.primaryResident === 'No' && !isValidPhoneNumber(primaryPhone)) {
			errors.primaryPhone = 'Please enter a valid phone number for the primary contact';
		}

		if (values.primaryResident && values.primaryResident === 'No' && !primaryFirstName) {
			errors.primaryFirstName = 'Please enter the first name for the primary contact';
		}

		if (values.primaryResident && values.primaryResident === 'No' && !primaryLastName) {
			errors.primaryLastName = 'Please enter the last name for the primary contact';
		}

		if (!serviceAddress) {
			errors.serviceAddress = 'Please enter your service address';
		}

		if (!serviceCity) {
			errors.serviceCity = 'Please enter your city';
		}

		if (!serviceState) {
			errors.serviceState = 'Please enter your state';
		}

		if (!isValidPostal(serviceZip)) {
			errors.zipPostal = 'Please enter a valid postal code';
		}

		return errors;
	};

	const validateForm = (DescriptionofIssue) => {
		let errors = [];

		if (!DescriptionofIssue) {
			errors.DescriptionofIssue = 'Please enter a description of the issue';
		}

		return errors;
	};

	const onFormSubmit = (e) => {
		e.preventDefault();

		const errors = validateForm(values.DescriptionofIssue);
		if (Object.keys(errors).length > 0) {
			setErrors(errors);
			setStatus('');
		} else {
			const data = new FormData(e.target);
			setErrors({});
			setStatus('pending');

			axios
				.post(eloquaTrackingUrl, data)
				.then((res) => {
					setStatus('success');
					setValues({ serviceZip: '' });
					setStep(4);
					setUpdate(true);
				})
				.catch(setStatus(''));
		}
	};

	const onModalCloseClick = () => {
		setModalOpen(false);
		setStep(3);
		setStatus('');
	};

	return (
		<EloquaForm
			elqFormName='service-request'
			onFormSubmit={(e) => onFormSubmit(e)}
			elqCookieWrite={values.primaryResident ? 1 : 0}
			className={styles.serviceRequest}
			utm={utm}
		>
			<input
				type='hidden'
				name='url'
				value={path}
			/>
			<input
				type='hidden'
				name='localPhone'
				value={serviceInfo.phone}
			/>
			<input
				type='hidden'
				name='localEmail'
				value={serviceInfo.email}
			/>

			{status === 'loading' && <LoadingOverlay fixed={false} />}

			<Row>
				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 1 ? { display: 'none' } : null}
				>
					<TextInput
						id='firstName'
						labelText='First Name'
						onChange={(e) => onInputChange(e)}
						required={true}
						error={errors.firstName ? true : false}
						errorText={errors.firstName}
						value={values.firstName || ''}
						autoComplete='given-name'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 1 ? { display: 'none' } : null}
				>
					<TextInput
						id='lastName'
						labelText='Last Name'
						onChange={(e) => onInputChange(e)}
						required={true}
						error={errors.lastName ? true : false}
						errorText={errors.lastName}
						value={values.lastName || ''}
						autoComplete='family-name'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 1 ? { display: 'none' } : null}
				>
					<TextInput
						id='emailAddress'
						labelText='Email Address'
						onChange={(e) => onInputChange(e)}
						onBlur={(e) => onEmailBlur(e)}
						required={true}
						error={errors.emailAddress ? true : false}
						errorText={errors.emailAddress}
						value={values.emailAddress || ''}
						autoComplete='email'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 1 ? { display: 'none' } : null}
				>
					<TextInput
						type='tel'
						id='contactPhone'
						labelText='Contact Phone'
						onValueChange={(values) => onNumericInputChange('contactPhone', values.formattedValue)}
						autoComplete='tel-national'
						value={values.contactPhone || ''}
						format='(###) ###-####'
						allowEmptyFormatting
					/>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 1 ? { display: 'none' } : null}
				>
					<TextInput
						id='company'
						labelText='Company Name (if applicable)'
						onChange={(e) => onInputChange(e)}
						autoComplete='organization'
						value={values.company || ''}
					/>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 1 ? { display: 'none' } : null}
				>
					<Select
						labelText='Are you the primary resident of the property?'
						id='primaryResident'
						onChange={(e) => onInputChange(e)}
						value={values.primaryResident || ''}
					>
						<option value='Yes'>Yes</option>
						<option value='No'>No</option>
					</Select>
				</Column>

				<Column
					xs={12}
					style={step !== 1 ? { display: 'none' } : null}
				>
					<Button
						color='primary-alt'
						wide={true}
						onClick={() => onNextButtonClick()}
						className={styles.serviceRequest_SubmitBtn}
					>
						Next
					</Button>
				</Column>

				{values.primaryResident && values.primaryResident == 'No' && (
					<>
						<Column
							xs={12}
							md={6}
							lg={12}
							xl={6}
							style={step !== 2 ? { display: 'none' } : null}
						>
							<TextInput
								id='primaryFirstName'
								labelText='Primary Contact - First Name'
								onChange={(e) => onInputChange(e)}
								required={true}
								error={errors.primaryFirstName ? true : false}
								errorText={errors.primaryFirstName}
								value={values.primaryFirstName || ''}
								autoComplete='given-name'
							/>
						</Column>

						<Column
							xs={12}
							md={6}
							lg={12}
							xl={6}
							style={step !== 2 ? { display: 'none' } : null}
						>
							<TextInput
								id='primaryLastName'
								labelText='Primary Contact - Last Name'
								onChange={(e) => onInputChange(e)}
								required={true}
								error={errors.primaryLastName}
								errorText={errors.primaryLastName}
								value={values.primaryLastName || ''}
								autoComplete='family-name'
							/>
						</Column>

						<Column
							xs={12}
							style={step !== 2 ? { display: 'none' } : null}
						>
							<TextInput
								type='tel'
								id='primaryPhone'
								labelText='Primary Contact - Phone #'
								onValueChange={(values) => onNumericInputChange('primaryPhone', values.formattedValue)}
								required={true}
								error={errors.primaryPhone ? true : false}
								errorText={errors.primaryPhone}
								value={values.primaryPhone || ''}
								autoComplete='tel-national'
								format='(###) ###-####'
							/>
						</Column>
					</>
				)}

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 2 ? { display: 'none' } : null}
				>
					<TextInput
						id='serviceAddress'
						labelText='Service Address'
						onChange={(e) => onInputChange(e)}
						required={true}
						error={errors.serviceAddress ? true : false}
						errorText={errors.serviceAddress}
						value={values.serviceAddress || ''}
						autoComplete='address-line1'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 2 ? { display: 'none' } : null}
				>
					<TextInput
						id='serviceCity'
						labelText='Service City'
						onChange={(e) => onInputChange(e)}
						required={true}
						error={errors.serviceCity ? true : false}
						errorText={errors.serviceCity}
						value={values.serviceCity || ''}
						autoComplete='address-level2'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 2 ? { display: 'none' } : null}
				>
					<Select
						id='serviceState'
						labelText='Service State'
						onChange={(e) => onInputChange(e)}
						error={errors.serviceState ? true : false}
						errorText={errors.serviceState}
						value={values.serviceState || ''}
						required
						autoComplete='off'
					>
						<option value=''>Select...</option>
						<optgroup label='United States'>
							<option
								id='USA-AL'
								value='AL'
							>
								Alabama (AL)
							</option>
							<option
								id='USA-AK'
								value='AK'
							>
								Alaska (AK)
							</option>
							<option
								id='USA-AZ'
								value='AZ'
							>
								Arizona (AZ)
							</option>
							<option
								id='USA-AR'
								value='AR'
							>
								Arkansas (AR)
							</option>
							<option
								id='USA-CA'
								value='CA'
							>
								California (CA)
							</option>
							<option
								id='USA-CO'
								value='CO'
							>
								Colorado (CO)
							</option>
							<option
								id='USA-CT'
								value='CT'
							>
								Connecticut (CT)
							</option>
							<option
								id='USA-DE'
								value='DE'
							>
								Delaware (DE)
							</option>
							<option
								id='USA-DC'
								value='DC'
							>
								District of Columbia (DC)
							</option>
							<option
								id='USA-FL'
								value='FL'
							>
								Florida (FL)
							</option>
							<option
								id='USA-GA'
								value='GA'
							>
								Georgia (GA)
							</option>
							<option
								id='USA-GU'
								value='GU'
							>
								Guam (GU)
							</option>
							<option
								id='USA-HI'
								value='HI'
							>
								Hawaii (HI)
							</option>
							<option
								id='USA-ID'
								value='ID'
							>
								Idaho (ID)
							</option>
							<option
								id='USA-IL'
								value='IL'
							>
								Illinois (IL)
							</option>
							<option
								id='USA-IN'
								value='IN'
							>
								Indiana (IN)
							</option>
							<option
								id='USA-IA'
								value='IA'
							>
								Iowa (IA)
							</option>
							<option
								id='USA-KS'
								value='KS'
							>
								Kansas (KS)
							</option>
							<option
								id='USA-KY'
								value='KY'
							>
								Kentucky (KY)
							</option>
							<option
								id='USA-LA'
								value='LA'
							>
								Louisiana (LA)
							</option>
							<option
								id='USA-ME'
								value='ME'
							>
								Maine (ME)
							</option>
							<option
								id='USA-MD'
								value='MD'
							>
								Maryland (MD)
							</option>
							<option
								id='USA-MA'
								value='MA'
							>
								Massachusetts (MA)
							</option>
							<option
								id='USA-MI'
								value='MI'
							>
								Michigan (MI)
							</option>
							<option
								id='USA-MN'
								value='MN'
							>
								Minnesota (MN)
							</option>
							<option
								id='USA-MS'
								value='MS'
							>
								Mississippi (MS)
							</option>
							<option
								id='USA-MO'
								value='MO'
							>
								Missouri (MO)
							</option>
							<option
								id='USA-MT'
								value='MT'
							>
								Montana (MT)
							</option>
							<option
								id='USA-NE'
								value='NE'
							>
								Nebraska (NE)
							</option>
							<option
								id='USA-NV'
								value='NV'
							>
								Nevada (NV)
							</option>
							<option
								id='USA-NH'
								value='NH'
							>
								New Hampshire (NH)
							</option>
							<option
								id='USA-NJ'
								value='NJ'
							>
								New Jersey (NJ)
							</option>
							<option
								id='USA-NM'
								value='NM'
							>
								New Mexico (NM)
							</option>
							<option
								id='USA-NY'
								value='NY'
							>
								New York (NY)
							</option>
							<option
								id='USA-NC'
								value='NC'
							>
								North Carolina (NC)
							</option>
							<option
								id='USA-ND'
								value='ND'
							>
								North Dakota (ND)
							</option>
							<option
								id='USA-OH'
								value='OH'
							>
								Ohio (OH)
							</option>
							<option
								id='USA-OK'
								value='OK'
							>
								Oklahoma (OK)
							</option>
							<option
								id='USA-OR'
								value='OR'
							>
								Oregon (OR)
							</option>
							<option
								id='USA-PA'
								value='PA'
							>
								Pennsylvania (PA)
							</option>
							<option
								id='USA-PR'
								value='PR'
							>
								Puerto Rico (PR)
							</option>
							<option
								id='USA-RI'
								value='RI'
							>
								Rhode Island (RI)
							</option>
							<option
								id='USA-SC'
								value='SC'
							>
								South Carolina (SC)
							</option>
							<option
								id='USA-SD'
								value='SD'
							>
								South Dakota (SD)
							</option>
							<option
								id='USA-TN'
								value='TN'
							>
								Tennessee (TN)
							</option>
							<option
								id='USA-TX'
								value='TX'
							>
								Texas (TX)
							</option>
							<option
								id='USA-UT'
								value='UT'
							>
								Utah (UT)
							</option>
							<option
								id='USA-VT'
								value='VT'
							>
								Vermont (VT)
							</option>
							<option
								id='USA-VA'
								value='VA'
							>
								Virginia (VA)
							</option>
							<option
								id='USA-VI'
								value='VI'
							>
								Virgin Islands (VI)
							</option>
							<option
								id='USA-WA'
								value='WA'
							>
								Washington (WA)
							</option>
							<option
								id='USA-WV'
								value='WV'
							>
								West Virginia (WV)
							</option>
							<option
								id='USA-WI'
								value='WI'
							>
								Wisconsin (WI)
							</option>
							<option
								id='USA-WY'
								value='WY'
							>
								Wyoming (WY)
							</option>
						</optgroup>
						<optgroup label='Canada'>
							<option
								id='CAN-AB'
								value='AB'
							>
								Alberta (AB)
							</option>
							<option
								id='CAN-BC'
								value='BC'
							>
								British Columbia (BC)
							</option>
							<option
								id='CAN-MB'
								value='MB'
							>
								Manitoba (MB)
							</option>
							<option
								id='CAN-NB'
								value='NB'
							>
								New Brunswick (NB)
							</option>
							<option
								id='CAN-NL'
								value='NL'
							>
								Newfoundland and Labrador (NL)
							</option>
							<option
								id='CAN-NT'
								value='NT'
							>
								Northwest Territories (NT)
							</option>
							<option
								id='CAN-NS'
								value='NS'
							>
								Nova Scotia (NS)
							</option>
							<option
								id='CAN-NU'
								value='NU'
							>
								Nunavut (NU)
							</option>
							<option
								id='CAN-PE'
								value='PE'
							>
								Prince Edward Island (PE)
							</option>
							<option
								id='CAN-SK'
								value='SK'
							>
								Saskatchewan (SK)
							</option>
							<option
								id='CAN-ON'
								value='ON'
							>
								Ontario (ON)
							</option>
							<option
								id='CAN-QC'
								value='QC'
							>
								Quebec (QC)
							</option>
							<option
								id='CAN-YT'
								value='YT'
							>
								Yukon (YT)
							</option>
						</optgroup>
					</Select>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 2 ? { display: 'none' } : null}
				>
					<TextInput
						id='serviceZip'
						labelText='Service Postal Code'
						onChange={(e) => onInputChange(e)}
						required={true}
						error={errors.serviceZip ? true : false}
						errorText={errors.serviceZip}
						value={values.serviceZip || ''}
						autoComplete='postal-code'
					/>
				</Column>

				<Column
					xs={12}
					style={step !== 2 ? { display: 'none' } : null}
				>
					<Button
						color='primary-alt'
						wide={true}
						onClick={() => onNextButtonClick()}
						className={styles.serviceRequest_SubmitBtn}
					>
						Next
					</Button>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 3 ? { display: 'none' } : null}
				>
					<TextInput
						id='brandProduct'
						labelText='Brand/Product'
						onChange={(e) => onInputChange(e)}
						value={values.brandProduct || ''}
						autoComplete='off'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 3 ? { display: 'none' } : null}
				>
					<TextInput
						id='serialNumber'
						labelText='Serial Number / NFRC'
						onChange={(e) => onInputChange(e)}
						value={values.serialNumber || ''}
						autoComplete='off'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 3 ? { display: 'none' } : null}
				>
					<TextInput
						id='purchaseOrder'
						labelText='Purchase Order #'
						onChange={(e) => onInputChange(e)}
						value={values.purchaseOrder || ''}
						autoComplete='off'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 3 ? { display: 'none' } : null}
				>
					<TextInput
						id='purchaseYear'
						labelText='Vintage (Purchase Year)'
						onChange={(e) => onInputChange(e)}
						value={values.purchaseYear || ''}
						autoComplete='off'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
					lg={12}
					xl={6}
					style={step !== 3 ? { display: 'none' } : null}
				>
					<TextInput
						id='quantity'
						type='number'
						labelText='Quantity of Product w/ Issue(s)'
						onChange={(e) => onInputChange(e)}
						value={values.quantity || ''}
						autoComplete='off'
					/>
				</Column>

				<Column
					xs={12}
					style={step !== 3 ? { display: 'none' } : null}
				>
					<TextArea
						id='DescriptionofIssue'
						labelText='Description of Issue'
						onChange={(e) => onInputChange(e)}
						required={true}
						error={errors.DescriptionofIssue}
						errorText={errors.DescriptionofIssue}
						value={values.DescriptionofIssue || ''}
						rows={3}
						autoComplete='off'
					/>
				</Column>

				<Column
					xs={12}
					style={step !== 3 ? { display: 'none' } : null}
				>
					<TextArea
						id='comments'
						labelText='Additional Comments'
						onChange={(e) => onInputChange(e)}
						value={values.comments || ''}
						rows={2}
						autoComplete='off'
					/>
				</Column>

				<Column
					xs={12}
					style={step !== 3 ? { display: 'none' } : null}
				>
					<Button
						color='primary-alt'
						type='submit'
						wide={true}
						disabled={status === 'disabled' || status === 'success'}
						className={styles.serviceRequest_SubmitBtn}
					>
						{status === 'disabled' || status === 'success' ? 'Processing...' : 'Submit Service Request'}
					</Button>
				</Column>

				{status === 'success' && (
					<Column xs={12}>
						<div className={styles.serviceRequest_confirmationMsg}>Thank you! We have received your request and will get in touch soon.</div>
					</Column>
				)}
			</Row>

			<VisualJourneyModal
				isOpen={modalOpen}
				onClose={onModalCloseClick}
			/>
		</EloquaForm>
	);
};

ServiceRequest.propTypes = {
	path: PropTypes.string,
	utm: PropTypes.object,
};

export default ServiceRequest;
